import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import LinearProgress from '@material-ui/core/LinearProgress'
import { makeStyles } from '@material-ui/core/styles'
import { PageHeader } from 'components/page-header'
import { Tabs, Tab } from 'components/tabs'
import InlineCountBadge from 'components/inline-count-badge'
import TablePagination from 'components/pagination/table-pagination'
import { useVirtualCoachingRequests } from './api-hooks'
import VirtualCoachingCoachPlusGate from './coach-plus-gate'
import LoadError from './load-error'
import RequestsTable from './requests-table'
import { useStyles as useSharedStyles } from './styles'
import { isRequestTab, partitionRequestsByTab, REQUEST_TABS } from './utils'

const ROWS_PER_PAGE = 25

const useStyles = makeStyles(theme => ({
  container: {
    boxSizing: 'border-box',
    padding: theme.spacing(3, 4.5, 2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 1, 2),
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}))

const VirtualCoachingRequests = () => {
  const classes = useStyles()
  const sharedClasses = useSharedStyles()
  const history = useHistory()
  const location = useLocation()
  const [page, setPage] = useState(0)
  const {
    data,
    previousData,
    loading,
    error,
    refetch,
  } = useVirtualCoachingRequests()

  const tabParam = new URLSearchParams(location.search).get('tab')
  const tab = isRequestTab(tabParam) ? tabParam : REQUEST_TABS.READY

  useEffect(() => {
    setPage(0)
  }, [tab])

  const requests = (data ?? previousData)?.virtualCoachingRequests
  const partitioned = useMemo(() => partitionRequestsByTab(requests), [
    requests,
  ])

  const tabRequests = partitioned[tab]
  const pageCount = Math.ceil(tabRequests.length / ROWS_PER_PAGE) || 1
  const visibleRequests = tabRequests.slice(
    page * ROWS_PER_PAGE,
    (page + 1) * ROWS_PER_PAGE,
  )

  const handleTabChange = (_, nextTab) => {
    if (nextTab === tab) return
    history.push(`${location.pathname}?tab=${nextTab}`)
  }

  const tabs = (
    <Tabs value={tab} onChange={handleTabChange} labelGapSpacing={3}>
      <Tab label="Purchased" value={REQUEST_TABS.PURCHASED} />
      <Tab
        label={
          <InlineCountBadge count={partitioned[REQUEST_TABS.READY].length}>
            Ready to analyze
          </InlineCountBadge>
        }
        value={REQUEST_TABS.READY}
      />
      <Tab label="Complete" value={REQUEST_TABS.COMPLETE} />
    </Tabs>
  )

  const isInitialLoad = loading && !requests

  return (
    <>
      <PageHeader title="Virtual Coaching" tabs={tabs} />
      <Box className={classes.container}>
        {isInitialLoad ? <LinearProgress color="primary" /> : null}
        {!loading && !requests ? (
          <LoadError
            error={error}
            title="We couldn't load your requests"
            onRetry={() => refetch()}
          />
        ) : null}
        {requests ? (
          <Box className={sharedClasses.tableShell}>
            {tabRequests.length > ROWS_PER_PAGE ? (
              <Box className={classes.toolbar}>
                <TablePagination
                  page={page}
                  pageCount={pageCount}
                  rowsPerPage={ROWS_PER_PAGE}
                  totalCount={tabRequests.length}
                  onPageChange={setPage}
                  alwaysShowCount
                />
              </Box>
            ) : null}
            <RequestsTable requests={visibleRequests} tab={tab} />
          </Box>
        ) : null}
      </Box>
    </>
  )
}

const VirtualCoachingRequestsPage = () => (
  <VirtualCoachingCoachPlusGate showEmptyRequests>
    <VirtualCoachingRequests />
  </VirtualCoachingCoachPlusGate>
)

export default VirtualCoachingRequestsPage
